import { useCallback, useMemo, useState } from 'react';

import { useMultiSelect } from '@crossnokaye/ux-common';

import { TableColumnConfig } from '@repo/sdk/models/components';

interface UseEquipmentBulkEditColumnSelectionOptions {
  columns: TableColumnConfig[];
  selectedColumnIds: string[];
  onSelectionChange: (ids: string[]) => void;
  dirtyColumnIds?: Set<string>;
  disabledColumnIds?: Set<string>;
  onDiscardColumns?: (columnIds: string[]) => void;
}

interface ColumnDiscardDialog {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  columnsToDiscard: string[];
}

interface UseEquipmentBulkEditColumnSelectionReturn {
  handleToggle: (columnId: string) => void;
  getColumnLabel: (columnId: string) => string;
  discardDialog: ColumnDiscardDialog;
}

/**
 * useEquipmentBulkEditColumnSelection
 *
 * Encapsulates the column selection logic with dirty-check interception.
 * Wraps `useMultiSelect` and intercepts deselection of dirty columns,
 * deferring the selection change until the user confirms via a discard dialog.
 */
export const useEquipmentBulkEditColumnSelection = ({
  columns,
  selectedColumnIds,
  onSelectionChange,
  dirtyColumnIds,
  disabledColumnIds,
  onDiscardColumns,
}: UseEquipmentBulkEditColumnSelectionOptions): UseEquipmentBulkEditColumnSelectionReturn => {
  // Dialog state for dirty column deselection warning
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<string[] | null>(null);
  const [columnsToDiscard, setColumnsToDiscard] = useState<string[]>([]);

  // Exclude disabled columns from the "all" list so Select All skips them
  const allColumnIds = useMemo(
    () =>
      columns
        .filter((col) => col.canEdit && !disabledColumnIds?.has(col.columnId))
        .map((col) => col.columnId),
    [columns, disabledColumnIds]
  );

  const { toggle } = useMultiSelect({
    selections: selectedColumnIds,
    setValue: (selections) => onSelectionChange(selections),
    clear: () => onSelectionChange([]),
    allItems: allColumnIds,
  });

  // Resolve column IDs to display labels for the dialog
  const getColumnLabel = useCallback(
    (columnId: string) => {
      const col = columns.find((c) => c.columnId === columnId);
      return col?.label ?? columnId;
    },
    [columns]
  );

  // Intercept individual column toggle to check for dirty deselection
  const handleToggle = useCallback(
    (columnId: string) => {
      const isDeselecting = selectedColumnIds.includes(columnId);
      const isDirty = dirtyColumnIds?.has(columnId);

      if (isDeselecting && isDirty) {
        setPendingSelection(selectedColumnIds.filter((id) => id !== columnId));
        setColumnsToDiscard([columnId]);
        setDiscardDialogOpen(true);
      } else {
        toggle(columnId);
      }
    },
    [selectedColumnIds, dirtyColumnIds, toggle]
  );

  // Confirm discard: apply the pending selection and revert form values
  const handleConfirmDiscard = useCallback(() => {
    if (pendingSelection !== null) {
      onSelectionChange(pendingSelection);
    }
    if (columnsToDiscard.length > 0) {
      onDiscardColumns?.(columnsToDiscard);
    }
    setPendingSelection(null);
    setColumnsToDiscard([]);
  }, [pendingSelection, columnsToDiscard, onSelectionChange, onDiscardColumns]);

  return {
    handleToggle,
    getColumnLabel,
    discardDialog: {
      open: discardDialogOpen,
      onOpenChange: setDiscardDialogOpen,
      onConfirm: handleConfirmDiscard,
      columnsToDiscard,
    },
  };
};
